import React, { useState, useEffect } from "react";
import { Card, Descriptions, Tag, Button, Space, Divider, Row, Col, Typography, Select, message } from "antd";
import { PrinterOutlined, EditOutlined, ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchProductQuery, useUpdateProductMutation } from "./label.service";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [labelHeight, setLabelHeight] = useState("40mm");
  const [updateProduct] = useUpdateProductMutation();

  const { data: productData, isLoading, refetch } = useFetchProductQuery(id);
  const product = productData?.data?.product;

  // Load saved height from product
  useEffect(() => {
    if (product?.labelConfig?.height) {
      setLabelHeight(`${product.labelConfig.height}mm`);
    }
  }, [product]);

  const getAllergens = () => {
    const standardAllergens = product?.allergens?.map(a => a.name) || [];
    const customAllergens = product?.customAllergens?.map(a => a.name) || [];
    return [...standardAllergens, ...customAllergens];
  };

  const getMayContain = () => {
    const standard = product?.allergens?.filter(a => a.type === "MAY_CONTAIN") || [];
    const custom = product?.customAllergens?.filter(a => a.type === "MAY_CONTAIN") || [];
    return [...standard.map(a => a.name), ...custom.map(a => a.name)];
  };

  const getExpiryInfo = () => {
    if (product?.customExpiryRule?.enabled) {
      return `${product.customExpiryRule.days} days (does not include day of production)`;
    }
    return "Standard shelf life";
  };

  // Calculate expiry date
  const expiryDays = product?.customExpiryRule?.enabled ? product.customExpiryRule.days : 4;
  const expiryDate = moment().add(expiryDays, 'days');
  const formattedDate = expiryDate.format('ddd DD/MM/YY');

  const heightOptions = ["40mm", "50mm", "60mm", "70mm", "80mm"];

  // Handle height change and save to backend
  const handleHeightChange = async (value) => {
    const heightValue = parseInt(value);
    setLabelHeight(value);
    
    try {
      await updateProduct({
        id,
        data: {
          labelConfig: {
            ...product?.labelConfig,
            height: heightValue
          }
        }
      }).unwrap();
      
      message.success("Label height saved successfully");
      refetch();
    } catch (error) {
      message.error("Failed to save label height");
      // Revert on error
      if (product?.labelConfig?.height) {
        setLabelHeight(`${product.labelConfig.height}mm`);
      }
    }
  };

  if (isLoading) return <Card loading />;
  if (!product) return <Card>Product not found</Card>;

  return (
    <div style={{ padding: "24px", background: "#f0f2f5", minHeight: "100vh" }}>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/label/category")}
            />
            <Title level={4} style={{ margin: 0 }}>
              Product Info
            </Title>
          </Space>
        }
        extra={
          <Space>
            {/* <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => navigate(`/label/food-date-label`)}
            >
              Print Label
            </Button> */}
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/label/product/${id}/edit`)}
            >
              Edit
            </Button>
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Name">
                <Text strong>{product.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="SKU">
                {product.sku || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {product.categoryId?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Use">
                {product.categoryId?.usage || "Prep"}
              </Descriptions.Item>
              <Descriptions.Item label="State">
                <Tag color={product.isActive ? "green" : "red"}>
                  {product.isActive ? "Active" : "Inactive"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Version">
                {product.version || "1.0"}
              </Descriptions.Item>
              <Descriptions.Item label="Custom Expiry Rule">
                {getExpiryInfo()}
              </Descriptions.Item>
              <Descriptions.Item label="Storage Location">
                {product.storageLocation || "—"}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          <Col span={12}>
            <Card
              title="Category shown on devices:"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Tag color={product.categoryId?.color || "blue"} style={{ fontSize: "14px" }}>
                {product.categoryId?.name}
              </Tag>
            </Card>

            <Card title="Allergens" size="small">
              {getAllergens().length > 0 && (
                <>
                  <div>
                    <Text strong>ALLERGENS:</Text>
                    <div style={{ marginTop: 8 }}>
                      {getAllergens().map(allergen => (
                        <Tag key={allergen} color="red" style={{ marginBottom: 4 }}>
                          {allergen}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {getMayContain().length > 0 && (
                <>
                  <Divider style={{ margin: "12px 0" }} />
                  <div>
                    <Text strong>MAY CONTAIN:</Text>
                    <div style={{ marginTop: 8 }}>
                      {getMayContain().map(allergen => (
                        <Tag key={allergen} color="orange" style={{ marginBottom: 4 }}>
                          {allergen}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {(getAllergens().length === 0 && getMayContain().length === 0) && (
                <Text type="secondary">No allergens listed</Text>
              )}
            </Card>
          </Col>
        </Row>

        <Divider />

        {/* Label Preview Section */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0 }}>LABEL PREVIEW</Title>
            {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Text>Height</Text>
              <Select 
                value={labelHeight} 
                onChange={handleHeightChange}
                style={{ width: "100px" }}
              >
                {heightOptions.map(h => (
                  <Option key={h} value={h}>{h}</Option>
                ))}
              </Select>
            </div> */}
          </div>
          
          <Row justify="center">
            <Col>
              <div 
                style={{ 
                  width: "300px",
                  background: "white",
                  borderRadius: "4px",
                  border: "1px solid #d9d9d9",
                  fontFamily: "Roboto, monospace",
                  height: labelHeight === "40mm" ? "auto" : "auto",
                  minHeight: labelHeight === "40mm" ? "180px" : labelHeight === "50mm" ? "220px" : labelHeight === "60mm" ? "260px" : "300px"
                }}
              >
                <div style={{ padding: "12px" }}>
                  {/* Top Half */}
                  <div style={{ textAlign: "center", fontWeight: "bold" }}>
                    <div style={{ 
                      textTransform: "uppercase", 
                      fontSize: "16px",
                      marginBottom: "8px"
                    }}>
                      {product.name}
                    </div>
                    <div style={{ 
                      backgroundColor: "black", 
                      color: "white", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "space-between",
                      padding: "4px 8px",
                      borderRadius: "2px"
                    }}>
                      <div style={{ fontSize: "10px" }}>USE<br/>BY:</div>
                      <div style={{ fontSize: "14px" }}>{formattedDate.split(' ')[0]}</div>
                      <div style={{ fontSize: "14px" }}>{formattedDate.split(' ')[1]}</div>
                    </div>
                  </div>

                  {/* Middle */}
                  <div style={{ 
                    marginTop: "8px", 
                    display: "flex", 
                    fontWeight: "bold",
                    fontSize: "10px"
                  }}>
                    <div style={{ width: "60%", textTransform: "uppercase" }}>
                      PRINTED:
                    </div>
                    <div style={{ width: "40%", textAlign: "right" }}>
                    </div>
                  </div>

                  {/* Allergens */}
                  <div style={{ 
                    fontFamily: "Roboto", 
                    textTransform: "uppercase", 
                    fontWeight: "bold", 
                    fontSize: "11px",
                    marginTop: "8px"
                  }}>
                    <div>
                      ALLERGENS: {getAllergens().join(", ")}
                      {getMayContain().length > 0 && ` | M/C: ${getMayContain().join(", ")}`}
                    </div>
                  </div>

      

     
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Print Queue Info */}
        {/* <div style={{ marginTop: 24, background: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <InfoCircleOutlined style={{ color: "#1890ff" }} />
                <Text>Labels will be printed with production date and expiry information</Text>
              </Space>
            </Col>
            <Col>
              <Button 
                type="link" 
                onClick={() => navigate("/label/print-queue")}
              >
                View Print Queue →
              </Button>
            </Col>
          </Row>
        </div> */}
      </Card>
    </div>
  );
}